import { captureException } from '@sentry/react'
import { isAxiosError } from 'axios'
import { useState } from 'react'

import type { ReactElement } from 'react'

import ManageAuthorizationModal from '../../ManageAuthorizationModal'

import Button from '~/components/Buttons/Button'
import Label from '~/components/Label'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  action: string
  authKey: string
  description: string
  resource: string
  service: string
}

const AuthorizationDetailsGeneralInformation = ({
  action,
  authKey,
  description,
  resource,
  service,
}: Props): ReactElement => {
  /** Local state */

  const [isModalOpen, setIsModalOpen] = useState(false)

  /** Handlers */

  const handleModalState = (isOpen: boolean) => () => setIsModalOpen(isOpen)

  const handleSuccess = () => {
    setIsModalOpen(false)
    displayToast('Authorization successfully updated', 'success')
  }

  const handleFailure = (error: unknown) => {
    setIsModalOpen(false)

    if (isAxiosError(error)) {
      switch (error.response?.data.code) {
        case 409:
          displayToast('Authorization could not be updated; authorization key is already forged', 'error')
          break
        default:
          captureException(error)
          displayToast('Authorization could not be updated', 'error')
      }
    } else {
      captureException(error)
      displayToast('Authorization could not be updated', 'error')
    }
  }

  /** Render */

  return (
    <div className="auth-details-general-information">
      <div className="keys-container">
        <div className="key-value">
          <div className="input-container">
            <Label className="label" label="Key" />
            <span className="value-container" title={authKey}>
              {authKey}
            </span>
          </div>
        </div>
        <div className="service-container">
          <div className="input-container">
            <Label className="label" label="Service" />
            <span className="value-container" title={service}>
              {service}
            </span>
          </div>
          <div className="input-container">
            <Label className="label" label="Resource" />
            <span className="value-container">{resource}</span>
          </div>
          <div className="input-container">
            <Label className="label" label="Action" />
            <span className="value-container">{action}</span>
          </div>
        </div>
      </div>
      <div className="description-container">
        <div className="input-container">
          <Label className="label" label="Description" />
          <span className="value-container" title={description}>
            {description}
          </span>
        </div>
      </div>
      <div className="actions-container">
        <Button onClick={handleModalState(true)} outlined size="small">
          Edit
        </Button>
      </div>
      <ManageAuthorizationModal
        action={action}
        authKey={authKey}
        description={description}
        isOpen={isModalOpen}
        onClose={handleModalState(false)}
        onFailure={handleFailure}
        onSuccess={handleSuccess}
        resource={resource}
        service={service}
      />
    </div>
  )
}

export default AuthorizationDetailsGeneralInformation
